import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Header } from '../shared/header/header';
import { SupplierFilter } from '../shared/components/supplier-filter/supplier-filter';
import { Viewport } from '../shared/viewport';
import { PaymentService } from '../payment/payment.service';
import { DailyPayablesReportResponse } from '../payment/payment.type';
import { PlayerAuctionService } from '../player-auction/player-auction.service';
import { SellContractDailyReportResponse } from '../player-auction/player-auction.type';
import { SupplierService } from '../supplier/supplier.service';
import { SupplierActivityReportResponse, SupplierFunnel } from '../supplier/supplier.type';
import { StackChart, StackDay } from './charts/stack-chart';
import { LineChart, LineSeries } from './charts/line-chart';
import { ShareBar, SharePart } from './charts/share-bar';
import { CHART, dayRange, fmtFull } from './charts/chart-kit';

/**
 * The charts cover the last CHART_DAYS days of the range, and the page opens on
 * the last DEFAULT_DAYS days. Both are about cost, not taste: a report sums
 * every bought card of every contract in range, and older months hold millions
 * (measured on production: ~0.5s for 30 days, ~6s once July/August 2026 are in).
 */
const CHART_DAYS = 30;
const DEFAULT_DAYS = 30;

/** Today in the report's zone, YYYY-MM-DD (en-CA formats dates that way). */
function reportToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tehran' }).format(new Date());
}

/** A YYYY-MM-DD day shifted by `days`. */
function addDays(date: string, days: number): string {
  return new Date(Date.parse(date + 'T00:00:00Z') + days * 86_400_000).toISOString().slice(0, 10);
}

interface PayablesCharts {
  owed: StackDay[];
  unpaidRunning: { dates: string[]; series: LineSeries[] };
  paidMeter: SharePart[];
  owedTotal: number;
  sources: SharePart[];
  truncated: boolean;
}

interface SellCharts {
  billed: StackDay[];
  coins: { dates: string[]; series: LineSeries[] };
  collected: SharePart[];
  billedTotal: number;
  truncated: boolean;
}

interface SupplierCharts {
  signups: { dates: string[]; series: LineSeries[] };
  work: { dates: string[]; series: LineSeries[] };
  delivering: { dates: string[]; series: LineSeries[] };
  truncated: boolean;
}

type ReportTab = 'payables' | 'sell' | 'suppliers';

/**
 * Daily money reports. Read-only — paying happens on the Payments page.
 *
 * - Payables: what suppliers are owed per day, the combined total and each
 *   source (stock transfer runs, on the day they confirmed; manual buy
 *   contracts, on the day they finished), each split into paid and unpaid.
 * - Sell contracts: what finished sell contracts billed per day (the day they
 *   finished), split into paid and unpaid.
 * - Suppliers: how marketing is doing — registrations, first-time activations
 *   and suppliers adding stocks, opening buy contracts and delivering, per day;
 *   plus the funnel of the suppliers who registered in the range, overall and
 *   per referral source.
 *
 * Each tab also shows the totals of the whole filtered range, not only the page,
 * and charts over the last CHART_DAYS days of it. The page opens on the last
 * DEFAULT_DAYS days; clearing the From date shows all history (slower).
 */
@Component({
  selector: 'app-report',
  imports: [
    CommonModule, FormsModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule,
    MatProgressSpinnerModule, MatTooltipModule, MatTabsModule, MatPaginatorModule, Header, SupplierFilter,
    StackChart, LineChart, ShareBar,
  ],
  templateUrl: './report.html',
  styleUrl: './report.scss',
})
export class Report implements OnInit {
  private paymentService = inject(PaymentService);
  private playerAuctionService = inject(PlayerAuctionService);
  private supplierService = inject(SupplierService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  readonly isMobile$ = inject(Viewport).isMobile$;

  readonly pageSizeOptions = [25, 50, 100, 200];
  readonly chartDays = CHART_DAYS;
  readonly paidUnpaid = [
    { label: 'Paid', color: CHART.paid },
    { label: 'Unpaid', color: CHART.unpaid },
  ];

  payablesCharts: PayablesCharts | null = null;
  sellCharts: SellCharts | null = null;
  supplierCharts: SupplierCharts | null = null;
  /** While charts refetch they keep their previous render, dimmed. */
  chartsLoading = false;

  tab: ReportTab = 'payables';
  payables: DailyPayablesReportResponse | null = null;
  sell: SellContractDailyReportResponse | null = null;
  suppliers: SupplierActivityReportResponse | null = null;
  loading = false;
  error: string | null = null;
  pageIndex = 0;
  pageSize = 50;

  supplierId = '';
  fromDate = '';
  toDate = '';

  ngOnInit(): void {
    // Filters and the tab ride in the URL so a reload keeps them.
    const q = this.route.snapshot.queryParamMap;
    const tab = q.get('tab');
    this.tab = tab === 'sell' || tab === 'suppliers' ? tab : 'payables';
    this.supplierId = q.get('supplier') ?? '';
    this.fromDate = q.get('from') ?? '';
    this.toDate = q.get('to') ?? '';
    // No range in the URL: open on the last DEFAULT_DAYS days rather than all history.
    if (!q.has('from') && !q.has('to')) {
      this.fromDate = this.defaultFrom();
      this.syncUrl();
    }
    this.load();
    this.loadCharts();
  }

  private defaultFrom(): string {
    return addDays(reportToday(), -(DEFAULT_DAYS - 1));
  }

  /** The charts' window: the last CHART_DAYS days of the range, ending at To (or today). */
  private chartWindow(): { fromDate: string; toDate?: string; truncated: boolean } {
    const end = this.toDate || reportToday();
    const earliest = addDays(end, -(CHART_DAYS - 1));
    const truncated = !this.fromDate || this.fromDate < earliest;
    return { fromDate: truncated ? earliest : this.fromDate, toDate: this.toDate || undefined, truncated };
  }

  /** The charts' own fetch: the last CHART_DAYS days of the range, independent of table paging. */
  loadCharts(): void {
    this.chartsLoading = true;
    const { fromDate, toDate, truncated } = this.chartWindow();
    const range = { fromDate, toDate, pageIndex: 0, pageSize: CHART_DAYS + 1 };
    const done = () => { this.chartsLoading = false; };
    if (this.tab === 'payables') {
      this.paymentService.getDailyPayablesReport({ ...range, supplierId: this.supplierId || undefined }).subscribe({
        next: res => { this.payablesCharts = this.buildPayablesCharts(res, truncated); done(); },
        error: done,
      });
    } else if (this.tab === 'sell') {
      this.playerAuctionService.getSellContractsDailyReport(range).subscribe({
        next: res => { this.sellCharts = this.buildSellCharts(res, truncated); done(); },
        error: done,
      });
    } else {
      this.supplierService.getActivityReport(range).subscribe({
        next: res => { this.supplierCharts = this.buildSupplierCharts(res, truncated); done(); },
        error: done,
      });
    }
  }

  private buildPayablesCharts(res: DailyPayablesReportResponse, truncated: boolean): PayablesCharts | null {
    if (res.rows.length === 0) return null;
    const byDate = new Map(res.rows.map(r => [r.date, r]));
    const dates = dayRange(res.rows.map(r => r.date));
    const owed: StackDay[] = dates.map(date => {
      const r = byDate.get(date);
      return {
        date,
        segments: [r?.total.paid ?? 0, r?.total.unpaid ?? 0],
        details: r ? [
          { label: `transfer runs (${fmtFull(r.runs.count)})`, value: fmtFull(r.runs.payable) },
          { label: `buy contracts (${fmtFull(r.contracts.count)})`, value: fmtFull(r.contracts.payable) },
        ] : [],
      };
    });
    // Running total of what is still unpaid, by the day it was earned.
    let running = 0;
    const unpaid = dates.map(d => (running += byDate.get(d)?.total.unpaid ?? 0));
    // Shares come from the charted days, so they match the charts beside them.
    const sum = (f: (r: typeof res.rows[number]) => number) => res.rows.reduce((a, r) => a + f(r), 0);
    const owedTotal = sum(r => r.total.payable);
    return {
      owed,
      unpaidRunning: { dates, series: [{ label: 'Unpaid so far', color: CHART.unpaid, values: unpaid }] },
      paidMeter: [{ label: 'Paid', color: CHART.paid, value: sum(r => r.total.paid) }],
      owedTotal,
      sources: [
        { label: 'Transfer runs', color: CHART.series1, value: sum(r => r.runs.payable) },
        { label: 'Buy contracts', color: CHART.series2, value: sum(r => r.contracts.payable) },
      ],
      truncated,
    };
  }

  private buildSellCharts(res: SellContractDailyReportResponse, truncated: boolean): SellCharts | null {
    if (res.rows.length === 0) return null;
    const byDate = new Map(res.rows.map(r => [r.date, r]));
    const dates = dayRange(res.rows.map(r => r.date));
    const billed: StackDay[] = dates.map(date => {
      const r = byDate.get(date);
      return {
        date,
        segments: [r?.paid ?? 0, r?.unpaid ?? 0],
        details: r ? [{ label: 'contracts', value: fmtFull(r.count) }] : [],
      };
    });
    const billedTotal = res.rows.reduce((a, r) => a + r.amount, 0);
    return {
      billed,
      coins: {
        dates,
        series: [
          { label: 'Coins sent', color: CHART.series1, values: dates.map(d => byDate.get(d)?.sent_coins ?? 0) },
          { label: 'Coins received', color: CHART.series2, values: dates.map(d => byDate.get(d)?.received_coins ?? 0) },
        ],
      },
      collected: [{ label: 'Paid', color: CHART.paid, value: res.rows.reduce((a, r) => a + r.paid, 0) }],
      billedTotal,
      truncated,
    };
  }

  private buildSupplierCharts(res: SupplierActivityReportResponse, truncated: boolean): SupplierCharts | null {
    if (res.rows.length === 0) return null;
    const byDate = new Map(res.rows.map(r => [r.date, r]));
    const dates = dayRange(res.rows.map(r => r.date!));
    const values = (f: (r: SupplierActivityReportResponse['rows'][number]) => number) =>
      dates.map(d => { const r = byDate.get(d); return r ? f(r) : 0; });
    return {
      signups: {
        dates,
        series: [
          { label: 'Registered', color: CHART.series1, values: values(r => r.registered) },
          { label: 'Activated', color: CHART.series2, values: values(r => r.activated) },
        ],
      },
      work: {
        dates,
        series: [
          { label: 'Added a stock', color: CHART.series1, values: values(r => r.stock_suppliers) },
          { label: 'Opened a buy contract', color: CHART.series2, values: values(r => r.contract_suppliers) },
        ],
      },
      delivering: {
        dates,
        series: [{ label: 'Delivering suppliers', color: CHART.paid, values: values(r => r.delivering_suppliers) }],
      },
      truncated,
    };
  }

  /** Funnel steps of the range's signups, each a meter against everyone who registered. */
  funnelSteps(f: SupplierFunnel): { label: string; hint: string; parts: SharePart[] }[] {
    const step = (label: string, hint: string, value: number) =>
      ({ label, hint, parts: [{ label, color: CHART.series1, value }] });
    return [
      step('Added a payout method', 'Saved a bank card or wallet to be paid to.', f.with_payout),
      step('Activated', 'Added a stock or opened a manual buy contract.', f.activated),
      step('Delivered', 'Had a transfer run confirmed, or a buy contract that bought a card.', f.delivered),
      step('Got paid', 'Received at least one payment.', f.paid),
    ];
  }

  sourceLabel(source: string): string {
    return source || 'Not given';
  }

  days(n: number): string {
    return n < 1 ? 'less than a day' : n === 1 ? '1 day' : `${n} days`;
  }

  pct(part: number, whole: number): string {
    return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–';
  }

  /** Anything other than the default view (every supplier, the last DEFAULT_DAYS days). */
  get hasFilters(): boolean {
    return !!((this.tab === 'payables' && this.supplierId) || this.fromDate !== this.defaultFrom() || this.toDate);
  }

  private get current(): { total: number; timezone: string } | null {
    return this.tab === 'payables' ? this.payables : this.tab === 'sell' ? this.sell : this.suppliers;
  }

  get total(): number {
    return this.current?.total ?? 0;
  }

  get timezone(): string {
    return this.current?.timezone ?? 'Asia/Tehran';
  }

  load(): void {
    this.loading = true;
    this.error = null;
    const range = {
      fromDate: this.fromDate || undefined,
      toDate: this.toDate || undefined,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    };
    const fail = (err: any) => { this.error = err?.error?.error ?? 'Failed to load the report'; this.loading = false; };
    if (this.tab === 'payables') {
      this.paymentService.getDailyPayablesReport({ ...range, supplierId: this.supplierId || undefined }).subscribe({
        next: res => { this.payables = res; this.loading = false; },
        error: fail,
      });
    } else if (this.tab === 'sell') {
      this.playerAuctionService.getSellContractsDailyReport(range).subscribe({
        next: res => { this.sell = res; this.loading = false; },
        error: fail,
      });
    } else {
      this.supplierService.getActivityReport(range).subscribe({
        next: res => { this.suppliers = res; this.loading = false; },
        error: fail,
      });
    }
  }

  selectTab(tab: ReportTab): void {
    if (tab === this.tab) return;
    this.tab = tab;
    this.syncUrl();
    this.pageIndex = 0;
    this.load();
    this.loadCharts();
  }

  refresh(): void {
    this.load();
    this.loadCharts();
  }

  onSupplierChange(id: string): void {
    this.supplierId = id;
    this.filtersChanged();
  }

  filtersChanged(): void {
    this.syncUrl();
    this.pageIndex = 0;
    this.load();
    this.loadCharts();
  }

  /** Back to the default view: every supplier, the last DEFAULT_DAYS days. */
  clearFilters(): void {
    this.supplierId = '';
    this.fromDate = this.defaultFrom();
    this.toDate = '';
    this.filtersChanged();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.load();
  }

  private syncUrl(): void {
    this.router.navigate([], {
      queryParams: {
        tab: this.tab === 'payables' ? null : this.tab,
        supplier: this.supplierId || null,
        // '' (not null) keeps an explicitly cleared From — all history — across a reload.
        from: this.fromDate,
        to: this.toDate || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
